import { BaseModel } from '../_base/crud';

interface OwnerModel {
    _id : string,
    name : string,
    admin : boolean
}

export class AlertModel extends BaseModel {
    _id: string;
    title: string;
    customTitle: string; // New field for custom title
    description: string;
    alertId: string;
    bookmark: boolean;
    type: number;
    subtype: number;
    catch:boolean;
    comment:string;
    system: {
        _id: string,
        name: string,
        tsTable: string,
        manufacturer: String
    };
    tag: {
        connectGaps: boolean;
        calculated: boolean;
        _id: string,
        name: string,
        description: string,
        tagId: string,
        threshold: {
            lower: number,
            upper: number
        },
        opLimit: {
            lower: number,
            upper: number
        },
        thresholdAlertRange: {
            l: number,
            h: number
        },
        alarmLimit: {
            lower: number,
            upper: number
        },
        metaData: {
            contextMatchString: string,
            assetId: string
        },
        pidDiagrams: string[]
    };
    facility: string;
    owner: Partial<OwnerModel>;
    owner_name: string;
    state: number;
    severity: string;
    lastStatusChangedOn: string;
    severityValue: number;
    markedFalsePositiveOn: String;
    markedOPChangeOn: String;
    accumulationType: Number;
    ranges: [{
        from: Date,
        to: Date
    }];
    /** Selected recommendations by subtype (DQ / Threshold). Anomaly uses failures[].recommendations. */
    recommendations?: Record<string, string[]>;
    lastActiveFrom: Date;
    lastActiveTo: Date;
    userMarkedFailures: [{
        failure: String,
        recommendation: String,
        addedOn: Date
    }];
    customFailureReason: [{
        failure: String,
        recommendation: String,
        addedOn: Date
    }];
    defectId: String;
    status: string;
    statusValue: number;
    category: number;
    component: String;
    errorCode: any;

    createdTime: Date;
    confirmedTime: Date;
    dueDate: Date;

    range: {
        from: Date;
        to: Date;
    };

    facilityType: Number
    contributing_tags: Array<String>

    trigger: {
        threshold: number,
        threshold_window: number,       /* # of seconds */
        avg_anomalies: number[],
        failureConfig: {
            weightValue: number,
            failurePercentage: number,
        }
    };
    pythonEngine: boolean;
    failureAdded: boolean;
    failures: [{
        name: string,
        mode: string,
        range: [],
        score: number,
        approved: boolean,
        reason: string,
        tags: [],
        recommendations: [], /* ids */
        markedTime: Date;
        modifiedBy: [],
        time_to_failure: string,
        time_to_intervention?: string,
        bookmark: boolean,
        isVisible?: boolean,
        aprrovalOrRejectionTime?: Date,
        sapNotifications?: {
            id: string,
            notificationNo: string,
        }[]
    }];
    totalMonetaryValue: Number;
    validity : number;
    subscribers: [];
    reviewers: [];
    assignedDate: Date;
    opStateChangeTags: [{
        tag: '',
        status: undefined,
        approve: undefined,
        markedTime: undefined,
        reason: '',
        modifiedBy: []
    }];
    evidence: any;
    restoredRcaId: string;
    isSplitMergeInProgress?: boolean; // Optional field to track split/merge progress

    clear() {
        this._id = '';
        this.title = '';
        this.description = '';
        this.alertId = '';
        this.type = undefined;
        this.system = {
            _id: '',
            name: '',
            tsTable: '',
            manufacturer: ''
        };
        this.tag = {
            connectGaps: false,
            calculated : false,
            _id: '',
            name: '',
            description: '',
            tagId: '',
            threshold: {
                lower: undefined,
                upper: undefined
            },
            opLimit: {
                lower: undefined,
                upper: undefined
            },
            thresholdAlertRange: {
                l: undefined,
                h: undefined
            },
            alarmLimit: {
                lower: undefined,
                upper: undefined
            },
            metaData: {
                contextMatchString: '',
                assetId: ''
            },
            pidDiagrams: []
        };
        this.facility = '';
        this.owner = {
            _id: ''
        };
        this.owner_name = '';
        this.severity = '';
        this.severityValue = undefined;
        this.lastStatusChangedOn = '';
        this.status = '';
        this.statusValue = undefined;
        this.createdTime = undefined;
        this.confirmedTime = undefined;
        this.dueDate = undefined;
        this.markedFalsePositiveOn = '',
        this.markedOPChangeOn = '',
        this.accumulationType = undefined,
        this.ranges = [{
            from: undefined,
            to: undefined
        }],
            this.lastActiveFrom = undefined,
            this.lastActiveTo = undefined,
        this.userMarkedFailures = [{
            failure: '',
            recommendation: '',
            addedOn: undefined
        }],
        this.customFailureReason = [{
            failure: '',
            recommendation: '',
            addedOn: undefined
        }],
        this.defectId= ''
        this.range = {
            from: undefined,
            to: undefined
        };
        this.facilityType = undefined
        this.contributing_tags = []
        this.trigger = {
            threshold: undefined,
            threshold_window: undefined,
            avg_anomalies: [],
            failureConfig: {
                weightValue: undefined,
                failurePercentage: undefined,
            }
        }
        this.pythonEngine = false,
        this.failureAdded = false
        this.failures = [{
            name: '',
            mode: '',
            range: [],
            score: 0,
            approved: undefined,
            isVisible: undefined,
            aprrovalOrRejectionTime: undefined,
            reason: '',
            tags: [],
            recommendations: [], /* ids */
            markedTime: undefined,
            modifiedBy: [],
            time_to_failure: '',
            time_to_intervention: '',
            bookmark: false,
        }];
        this.validity = 0;
        this.subscribers = [];
        this.reviewers = [];
        this.assignedDate = undefined
        this.opStateChangeTags = [{
            tag: '',
            status: undefined,
            approve: undefined,
            markedTime: undefined,
            reason: '',
            modifiedBy: []
        }];
        this.evidence = {};
        this.restoredRcaId = "";
        this.isSplitMergeInProgress = false;
    }
}

export interface IAlertListFilter{
  dateRange?: { from: string | null; to: string | null; },
  filter?: {
    typeStateGroups?: { anomaly: number[]; dq: number[]; }[],
    catch?: boolean[],
    bookmark?: boolean[],
    subscribers?: string[],
    searchText?: string,
    dqType?: number[],
    dqAlertTagStatus?: boolean,
    subtype?: number[],
    isRcaAvailable?: boolean[],
    hasCases?: boolean[]
  },
  type?: number[],
  statusValue?: number[],
  owner?: any[],
  groupBy?: string,
  hierarchyFilter?: {
    facility?: string,
    unit?: string,
    system?: string
  },
  sort?: string[],
  page?: number,
  limit?: number,
  severityValue?: number[]
}

export enum RcaState {
    NotGenerated = 0,
    Generating = 1,
    InProgress = 2,
    Available = 3,
    Failed = 4,
    Cancelled = 5
}

export const CLOSED_OR_RESOLVED_STATUS_VALUES: readonly number[] = [5, 6, 7, 8];